import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface MediaLightboxProps {
  src: string;
  alt: string;
  onClose: () => void;
}

/**
 * Full-screen image viewer: click the backdrop or the close button, or press Escape, to dismiss.
 * Clicking the image itself toggles between fit-to-screen and a zoomed-in view (scrollable if it
 * overflows). Rendered via a portal so it isn't clipped by a scrolling ancestor (the message list).
 */
export function MediaLightbox({ src, alt, onClose }: MediaLightboxProps) {
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="media-lightbox-backdrop" onClick={onClose}>
      <button type="button" className="media-lightbox-close" onClick={onClose} aria-label="Close">
        &times;
      </button>
      <img
        src={src}
        alt={alt}
        className={zoomed ? "media-lightbox-image media-lightbox-image-zoomed" : "media-lightbox-image"}
        onClick={(event) => {
          event.stopPropagation();
          setZoomed((prev) => !prev);
        }}
      />
    </div>,
    document.body,
  );
}
